import { Fragment, useState, useRef, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import Select from 'react-select'
import { API_URL } from '../../../utils/Constants/URLS'
import 'primereact/resources/themes/lara-light-cyan/theme.css'
import Modal from 'react-bootstrap/Modal'
import { Toast } from 'primereact/toast'
import PageLoader from '../../../utils/PageLoader'

const FocReachedAgreement = ({ id, onDataUpdate, focLeadDetails: propFocLeadDetails }) => {
    const token = localStorage.getItem('token')
    const toast = useRef(null)
    const navigate = useNavigate()
    const [modalMessage, setModalMessage] = useState('')
    const [showSuccessModal, setShowSuccessModal] = useState(false)
    const [showDeclineModal, setShowDeclineModal] = useState(false)
    const [focLeadDetails, setFocLeadDetails] = useState(null)
    const [isCompleted, setIsCompleted] = useState(false)
    const [submitting, setSubmitting] = useState(false)
    const [salesOrderId, setSalesOrderId] = useState('')
    const [soStatus, setSoStatus] = useState('')
    const [soRejectionReason, setSoRejectionReason] = useState('')
    const [isEditing, setIsEditing] = useState(false)

    // Form states
    const [zeroDateOfDelivery, setZeroOfDateDelivery] = useState('')
    const [customerPONumber, setCustomerPONumber] = useState('')
    const [isPOAvailable, setIsPoAvailable] = useState('true')
    const [PODetails, setPoDetails] = useState('')
    const [POFilePath, setPoFilePath] = useState('')
    const [customerPODate, setCustomerPODate] = useState('')
    const [gsplGSTNumber, setGsplGSTNumber] = useState('')
    const [consultant, setConsultant] = useState('')
    const [currencyOfTransaction, setCurrencyOfTransaction] = useState('')
    const [checkShipping, setCheckShipping] = useState(false)
    const [billingAddress, setBillingAddress] = useState('')
    const [shippingAddress, setShippingAddress] = useState('')
    const [warrantyPeriod, setWarrantyPeriod] = useState('')
    const [remarks, setRemarks] = useState('')
    const [warrantyTerms, setWarrantyTerms] = useState('')
    const [bankGuaranteeDetails, setbankGuaranteeDetails] = useState([])
    const [commercialManager, setCommercialManager] = useState('')
    const [commercialManagerDetails, setCommercialManagerDetails] = useState([])
    const [typeOfTransaction, setTypeOfTransaction] = useState('')
    const [BOMFilePath, setBOMFilePath] = useState('')
    const [responsibilityOfTransportation, setResponsibilityOfTransportation] = useState('')
    const [priceBasis, setPriceBasis] = useState('')
    const [paymentTerms, setPaymentTerms] = useState([
        { type: '', percentage: '100', amount: '0' }
    ])
    const [lineItems, setLineItems] = useState([])

    const poFileInputRef = useRef(null)
    const bomFileInputRef = useRef(null)

    const bankGuaranteeDetailsData = [
        { value: 'NA', label: 'NA' },
        { value: 'ABG', label: 'ABG' },
        { value: 'CPBG', label: 'CPBG' },
        { value: 'PBG', label: 'PBG' }
    ]

    let totalDealValue = 0
    if (focLeadDetails) {
        if (focLeadDetails?.leadType === 'Direct') {
            const salesCommissionItem = focLeadDetails?.productRequirements?.crossSells?.find(item =>
                item?.model?.modelName?.toLowerCase().startsWith('sales commission')
            )
            if (salesCommissionItem) {
                totalDealValue = Number(salesCommissionItem?.value || 0)
            }
        } else {
            totalDealValue = Number(focLeadDetails?.productRequirements?.proposedDealAmount || 0)
        }
    }

    // Sync with parent focLeadDetails prop
    useEffect(() => {
        if (propFocLeadDetails) {
            setFocLeadDetails(propFocLeadDetails)

            if (propFocLeadDetails?.agreement?.stage === 'won') {
                setIsCompleted(true)
                // Fetch Sales Order details
                const fetchSO = async () => {
                    try {
                        const soRes = await fetch(`${API_URL}foc/sales-order/lead/${id}`, {
                            method: 'GET',
                            headers: {
                                'Content-Type': 'application/json',
                                Authorization: `Bearer ${token}`
                            }
                        })
                        const soResult = await soRes.json()
                        const soData = Array.isArray(soResult?.data)
                            ? soResult.data[0]
                            : soResult?.data || soResult

                        if (soData) {
                            setZeroOfDateDelivery(soData.zeroDateOfDelivery || '')
                            setCustomerPONumber(soData.customerPONumber || '')
                            setIsPoAvailable(String(soData.isPOAvailable ?? 'true'))
                            setCustomerPODate(soData.customerPODate || '')
                            setGsplGSTNumber(soData.gsplGSTNumber || '')
                            setConsultant(soData.consultant || '')
                            setCurrencyOfTransaction(soData.currencyOfTransaction || '')
                            setBillingAddress(soData.billingAddress || '')
                            setShippingAddress(soData.shippingAddress || '')
                            setWarrantyPeriod(soData.warrantyPeriod || '')
                            setWarrantyTerms(soData.warrantyTerms || '')
                            setRemarks(soData.remarks || '')
                            setResponsibilityOfTransportation(soData.responsibilityOfTransportation || '')
                            setTypeOfTransaction(soData.typeOfTransaction || '')
                            setPriceBasis(soData.priceBasis || '')
                            setbankGuaranteeDetails(soData.bankGuaranteeDetails || [])
                            setPaymentTerms(soData.paymentTerms || [])
                            setLineItems(soData.lineItems || [])
                            setPoFilePath(soData.POFilePath || '')
                            setBOMFilePath(soData.BOMFilePath || '')
                            setSalesOrderId(soData._id || soData.id || '')
                            setSoStatus(soData.status || '')
                            setSoRejectionReason(soData.reasonForRejection || soData.remarks || '')
                        }
                    } catch (err) {
                        console.error('Error fetching SO details:', err)
                    }
                }
                fetchSO()
            }
        }
    }, [propFocLeadDetails, id, token])

    // Fetch FOC Lead Details
    useEffect(() => {
        const fetchFocLead = async () => {
            try {
                const res = await fetch(`${API_URL}foc/lead/${id}`, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`
                    }
                })
                const response = await res.json()
                const leadData = response.data || response
                setFocLeadDetails(leadData)

                if (leadData?.agreement?.stage === 'won') {
                    setIsCompleted(true)
                    // Fetch Sales Order details for this lead to populate the view
                    try {
                        const soRes = await fetch(`${API_URL}foc/sales-order/lead/${id}`, {
                            method: 'GET',
                            headers: {
                                'Content-Type': 'application/json',
                                Authorization: `Bearer ${token}`
                            }
                        })
                        const soResult = await soRes.json()
                        // API returns an array inside data property, get the first element
                        const soData = Array.isArray(soResult?.data)
                            ? soResult.data[0]
                            : soResult?.data || soResult

                        if (soData) {
                            setZeroOfDateDelivery(soData.zeroDateOfDelivery || '')
                            setCustomerPONumber(soData.customerPONumber || '')
                            setIsPoAvailable(String(soData.isPOAvailable ?? 'true'))
                            setCustomerPODate(soData.customerPODate || '')
                            setGsplGSTNumber(soData.gsplGSTNumber || '')
                            setConsultant(soData.consultant || '')
                            setCurrencyOfTransaction(soData.currencyOfTransaction || '')
                            setBillingAddress(soData.billingAddress || '')
                            setShippingAddress(soData.shippingAddress || '')
                            setWarrantyPeriod(soData.warrantyPeriod || '')
                            setWarrantyTerms(soData.warrantyTerms || '')
                            setRemarks(soData.remarks || '')
                            setResponsibilityOfTransportation(soData.responsibilityOfTransportation || '')
                            setTypeOfTransaction(soData.typeOfTransaction || '')
                            setPriceBasis(soData.priceBasis || '')
                            setbankGuaranteeDetails(soData.bankGuaranteeDetails || [])
                            setPaymentTerms(soData.paymentTerms || [])
                            setLineItems(soData.lineItems || [])
                            // Also set file paths if needed, though they aren't displayed in the snippet
                            setPoFilePath(soData.POFilePath || '')
                            setBOMFilePath(soData.BOMFilePath || '')
                            setSalesOrderId(soData._id || soData.id || '')
                            setSoStatus(soData.status || '')
                            setSoRejectionReason(soData.reasonForRejection || soData.remarks || '')
                        }
                    } catch (err) {
                        console.error('Error fetching SO details:', err)
                    }
                }
            } catch (e) {
                console.error('Error fetching FOC lead:', e)
            }
        }

        if (id && !propFocLeadDetails) {
            fetchFocLead()
        }
    }, [id, token, propFocLeadDetails])

    // Fetch Commercial Managers only
    useEffect(() => {
        const fetchCommercialManagers = async () => {
            const url = `${API_URL}users/org-contacts/commercial-manager`
            try {
                const response = await fetch(url, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`
                    }
                })
                const result = await response.json()
                setCommercialManagerDetails(
                    result.data.docs.map(data => ({
                        value: data._id,
                        label: data.name
                    }))
                )
            } catch (error) {
                console.error('Error fetching commercial managers:', error)
            }
        }

        fetchCommercialManagers()
    }, [token])

    const handleCloseModal = () => {
        setShowSuccessModal(false)
        setShowDeclineModal(false)
        // Refresh parent data to update accordion status
        if (onDataUpdate) {
            onDataUpdate()
        }
        // Navigate to FOC leads list after successful submission
        if (showSuccessModal) {
            navigate('/foc')
        }
    }

    const updateFocLead = async () => {
        try {
            const res = await fetch(`${API_URL}foc/lead/${id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify({
                    agreement: {
                        stage: 'won'
                    }
                })
            })
            const response = await res.json()
            return response
        } catch (e) {
            console.error('Error updating FOC lead:', e)
            throw e
        }
    }

    const uploadFiles = async () => {
        const poFileInput = poFileInputRef.current
        const bomFileInput = bomFileInputRef.current

        let uploadedPOPath = null
        let uploadedBOMPath = null

        // Upload PO file if PO is available
        if (isPOAvailable === 'true') {
            const hasNewFile = poFileInput && poFileInput.files && poFileInput.files.length > 0

            // If editing and no new file, we can skip if we have existing path
            if (!hasNewFile && isEditing && POFilePath) {
                // Do nothing, loadedPOPath stays null, handleSubmit will use POFilePath
            } else if (!hasNewFile) {
                toast.current.show({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'Please upload PO file',
                    life: 3000
                })
                return null
            } else {
                // Upload new file
                try {
                    const myHeaders = new Headers()
                    myHeaders.append('Authorization', `Bearer ${token}`)
                    const formdata = new FormData()
                    formdata.append('pdf', poFileInput.files[0])
                    formdata.append('category', 'purchase-orders')

                    const requestOptions = {
                        method: 'POST',
                        headers: myHeaders,
                        body: formdata,
                        redirect: 'follow'
                    }

                    const uploadRes = await fetch(`${API_URL}file/upload`, requestOptions)
                    const uploadResult = await uploadRes.json()

                    if (uploadResult && uploadResult.data) {
                        if (typeof uploadResult.data === 'string') {
                            uploadedPOPath = uploadResult.data
                        } else if (uploadResult.data.filePath) {
                            uploadedPOPath = uploadResult.data.filePath
                        }
                    }

                    if (!uploadedPOPath) {
                        toast.current.show({
                            severity: 'error',
                            summary: 'Error',
                            detail: 'PO file upload failed',
                            life: 3000
                        })
                        return null
                    }

                    setPoFilePath(uploadedPOPath)
                } catch (err) {
                    toast.current.show({
                        severity: 'error',
                        summary: 'Error',
                        detail: 'PO file upload failed',
                        life: 3000
                    })
                    return null
                }
            }
        }

        // Upload BOM file if it's a Project
        if (focLeadDetails?.productRequirements?.natureOfSale === 'Project') {
            const hasNewFile = bomFileInput && bomFileInput.files && bomFileInput.files.length > 0

            // If editing and no new file, skip if existing path
            if (!hasNewFile && isEditing && BOMFilePath) {
                // Skip
            } else if (!hasNewFile) {
                toast.current.show({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'Please upload BOM file for Project type',
                    life: 3000
                })
                return null
            } else {
                try {
                    const myHeaders = new Headers()
                    myHeaders.append('Authorization', `Bearer ${token}`)
                    const formdata = new FormData()
                    formdata.append('pdf', bomFileInput.files[0])
                    formdata.append('category', 'bill-of-materials')

                    const requestOptions = {
                        method: 'POST',
                        headers: myHeaders,
                        body: formdata,
                        redirect: 'follow'
                    }

                    const uploadRes = await fetch(`${API_URL}file/upload`, requestOptions)
                    const uploadResult = await uploadRes.json()

                    if (uploadResult && uploadResult.data) {
                        if (typeof uploadResult.data === 'string') {
                            uploadedBOMPath = uploadResult.data
                        } else if (uploadResult.data.filePath) {
                            uploadedBOMPath = uploadResult.data.filePath
                        }
                    }

                    if (!uploadedBOMPath) {
                        toast.current.show({
                            severity: 'error',
                            summary: 'Error',
                            detail: 'BOM file upload failed',
                            life: 3000
                        })
                        return null
                    }

                    setBOMFilePath(uploadedBOMPath)
                } catch (err) {
                    toast.current.show({
                        severity: 'error',
                        summary: 'Error',
                        detail: 'BOM file upload failed',
                        life: 3000
                    })
                    return null
                }
            }
        }

        return { poPath: uploadedPOPath, bomPath: uploadedBOMPath }
    }

    const handleSubmit = async e => {
        e.preventDefault()

        // Check if at least one line item exists
        const lineItems = focLeadDetails?.productRequirements?.crossSells || []
        const activeLineItems = lineItems.filter(item => item.status !== 'deleted')

        if (activeLineItems.length === 0) {
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Please add at least one line item before creating Sales Order',
                life: 4000
            })
            return
        }

        // Basic validations
        if (zeroDateOfDelivery === '') {
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Please Select Zero Date Delivery',
                life: 3000
            })
            return
        }

        if (gsplGSTNumber === '') {
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Please Select GSPL GST Number',
                life: 3000
            })
            return
        }

        if (currencyOfTransaction === '') {
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Please Select Currency of Transaction',
                life: 3000
            })
            return
        }

        if (warrantyPeriod === '') {
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Please Enter Warranty Period from Date of commissioning',
                life: 3000
            })
            return
        }

        if (warrantyTerms === '') {
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Please Enter Warranty Period from last date of supply',
                life: 3000
            })
            return
        }

        if (responsibilityOfTransportation === '') {
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Please Select Responsibility of Transportation',
                life: 3000
            })
            return
        }

        if (typeOfTransaction === '') {
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Please Select Type of Transaction',
                life: 3000
            })
            return
        }

        if (!bankGuaranteeDetails || bankGuaranteeDetails.length === 0) {
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Please Select Bank Guarantee Details',
                life: 3000
            })
            return
        }

        if (billingAddress === '') {
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Please Enter Billing Address',
                life: 3000
            })
            return
        }

        if (!checkShipping && shippingAddress === '') {
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Please Enter Shipping Address',
                life: 3000
            })
            return
        }

        // Validate Commercial Manager selection
        if (!commercialManager || commercialManager === '') {
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Please Select a Commercial Manager',
                life: 3000
            })
            return
        }

        // Validate paymentTerms entries
        for (let i = 0; i < paymentTerms.length; i++) {
            const term = paymentTerms[i]
            if (!term.type || term.percentage === '' || term.amount === '') {
                toast.current?.show({
                    severity: 'error',
                    summary: 'Error',
                    detail: `Please complete all fields in Payment Term ${i + 1}`,
                    life: 3000
                })
                return
            }
        }

        // Total percentage must be exactly 100.00
        const totalPercentageRaw = paymentTerms.reduce(
            (acc, term) => acc + (parseFloat(term.percentage) || 0),
            0
        )
        const totalPercentage = Math.round(totalPercentageRaw * 100) / 100
        if (totalPercentage !== 100) {
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: `Total Payment Terms percentage must be exactly 100%. Currently it is ${totalPercentage.toFixed(2)}%.`,
                life: 4000
            })
            return
        }

        // PO fields validation
        let poDetailsToSend = undefined
        if (isPOAvailable === 'true') {
            if (customerPONumber === '') {
                toast.current?.show({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'Please Enter Customer PO Number',
                    life: 3000
                })
                return
            }
            if (customerPODate === '') {
                toast.current?.show({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'Please Select Customer PO Date',
                    life: 3000
                })
                return
            }
            poDetailsToSend = undefined
        } else {
            if (
                customerPONumber !== '' ||
                customerPODate !== '' ||
                (poFileInputRef.current &&
                    poFileInputRef.current.files &&
                    poFileInputRef.current.files.length > 0)
            ) {
                toast.current?.show({
                    severity: 'warn',
                    summary: 'Warning',
                    detail: 'PO Number, PO Date, and PO File should not be entered when PO is not available.',
                    life: 4000
                })
                return
            }
            poDetailsToSend = PODetails
        }

        // Show loader
        setSubmitting(true)

        try {
            // Upload files
            const uploadResults = await uploadFiles()
            if (!uploadResults) {
                setSubmitting(false)
                return
            }

            const { poPath, bomPath } = uploadResults

            // Ensure PO was uploaded if available
            // If editing, we allow reusing existing POFilePath
            if (isPOAvailable === 'true' && !poPath && !POFilePath) {
                toast.current?.show({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'PO file upload failed or missing',
                    life: 3000
                })
                setSubmitting(false)
                return
            }

            // Ensure BOM was uploaded for Project type
            // If editing, we allow reusing existing BOMFilePath
            if (focLeadDetails?.productRequirements?.natureOfSale === 'Project' && !bomPath && !BOMFilePath) {
                toast.current?.show({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'BOM file upload failed or missing',
                    life: 3000
                })
                setSubmitting(false)
                return
            }

            // Build payload for FOC Sales Order
            const salesOrderData = {
                lead: id,
                client: focLeadDetails?.client?._id || focLeadDetails?.client,
                zeroDateOfDelivery,
                customerPONumber,
                customerPODate,
                gsplGSTNumber,
                consultant,
                currencyOfTransaction,
                billingAddress,
                shippingAddress,
                warrantyPeriod,
                warrantyTerms,
                remarks,
                bankGuaranteeDetails,
                typeOfTransaction,
                responsibilityOfTransportation,
                priceBasis,
                paymentTerms,
                POFilePath: poPath || POFilePath,
                PODetails: poDetailsToSend,
                isPOAvailable
            }

            if (isEditing) {
                salesOrderData.status = 'pending' // Reset status to pending
            }

            if (focLeadDetails?.productRequirements?.natureOfSale === 'Project') {
                salesOrderData.BOMFilePath = bomPath || BOMFilePath
            }

            let res;
            if (isEditing && salesOrderId) {
                // Update existing FOC sales order
                res = await fetch(`${API_URL}foc/sales-order/${salesOrderId}`, {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`
                    },
                    body: JSON.stringify(salesOrderData)
                })
            } else {
                // Create FOC sales order
                const res = await fetch(`${API_URL}foc/sales-order`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`
                    },
                    body: JSON.stringify(salesOrderData)
                })
                const response = await res.json()

                if (response.error === false) {
                    const finalSalesOrderId = response?.data?.id

                    if (finalSalesOrderId) {
                        try {
                            // Request approval from Commercial Manager only
                            await assignCommercialManager(finalSalesOrderId)
                            setModalMessage(
                                isEditing
                                    ? 'Sales Order Updated and Commercial Manager Notified Successfully'
                                    : 'Sales Order Created and Commercial Manager Assigned Successfully'
                            )
                            setShowSuccessModal(true)
                            setIsEditing(false) // Exit edit mode

                            // Update FOC lead
                            await updateFocLead()
                        } catch (managerError) {
                            console.error('Commercial Manager assignment error:', managerError)
                            setModalMessage('Sales Order Processed but Commercial Manager Assignment Failed')
                            setShowDeclineModal(true)
                        }
                    } else {
                        setModalMessage('Sales Order Processed but Missing ID for Manager Assignment')
                        setShowDeclineModal(true)
                    }
                } else {
                    setModalMessage(response.message || 'Failed to Process Sales Order')
                    setShowDeclineModal(true)
                }
            }


        } catch (e) {
            console.error('handleSubmit error:', e)
            setModalMessage('Oops! Error while adding data')
            setShowDeclineModal(true)
        } finally {
            setSubmitting(false)
        }
    }

    const assignCommercialManager = async salesOrderId => {
        try {
            const res = await fetch(
                `${API_URL}foc/sales-order/${salesOrderId}/assign`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`
                    },
                    body: JSON.stringify({
                        commercialManager: commercialManager
                    })
                }
            )

            const response = await res.json()
            console.log('Commercial Manager assignment response:', response)
            return response
        } catch (error) {
            console.error('Error assigning commercial manager:', error)
            throw error
        }
    }

    const handlePaymentTermChange = (index, field, value) => {
        const updatedTerms = [...paymentTerms]

        if (field === 'type') {
            // Only allow changing the type, keep percentage at 100 and amount at 0
            updatedTerms[index].type = value
            updatedTerms[index].percentage = '100'
            updatedTerms[index].amount = '0'
        }
        // For FOC, we don't allow changing percentage or amount
        // They should always be 100% and 0 respectively

        setPaymentTerms(updatedTerms)
    }

    const addPaymentTerm = () => {
        setPaymentTerms([...paymentTerms, { type: '', percentage: '100', amount: '0' }])
    }

    const removePaymentTerm = index => {
        setPaymentTerms(paymentTerms.filter((_, i) => i !== index))
    }

    useEffect(() => {
        if (checkShipping) {
            setShippingAddress(billingAddress)
        }
    }, [checkShipping, billingAddress])

    // Helper function to format date
    const formatDate = (dateString) => {
        if (!dateString) return '-'
        try {
            return new Date(dateString).toLocaleDateString('en-GB', {
                day: '2-digit',
                month: 'short',
                year: 'numeric'
            })
        } catch {
            return dateString
        }
    }

    return (
        <Fragment>
            <div className='relative'>
                {submitting && (
                    <div className='absolute inset-0 z-50 flex items-center justify-center'>
                        <PageLoader />
                    </div>
                )}
                {isCompleted && !isEditing ? (
                    // Completed View - Read Only Display
                    <div className='completed-stage-view'>
                        {soStatus === 'rejected' && (
                            <div className="alert alert-danger mb-4">
                                <h5 className="alert-heading">Sales Order Rejected</h5>
                                <p className="mb-2"><strong>Reason:</strong> {soRejectionReason}</p>
                                <button
                                    type="button"
                                    className="btn btn-primary btn-sm"
                                    onClick={() => setIsEditing(true)}
                                >
                                    Edit Sales Order
                                </button>
                            </div>
                        )}
                        <div className='row g-3'>

                            <div className='col-md-4'>
                                <div className='mb-3'>
                                    <label className='form-label text-muted fw-semibold'>Zero Date Delivery</label>
                                    <p className='mb-0 fs-6'>{formatDate(zeroDateOfDelivery)}</p>
                                </div>
                            </div>
                            <div className='col-md-4'>
                                <div className='mb-3'>
                                    <label className='form-label text-muted fw-semibold'>PO Available</label>
                                    <p className='mb-0 fs-6'>{isPOAvailable === 'true' ? 'Yes' : 'No'}</p>
                                </div>
                            </div>
                            {isPOAvailable === 'true' && (
                                <>
                                    <div className='col-md-4'>
                                        <div className='mb-3'>
                                            <label className='form-label text-muted fw-semibold'>Customer PO Number</label>
                                            <p className='mb-0 fs-6'>{customerPONumber || '-'}</p>
                                        </div>
                                    </div>
                                    <div className='col-md-4'>
                                        <div className='mb-3'>
                                            <label className='form-label text-muted fw-semibold'>Customer PO Date</label>
                                            <p className='mb-0 fs-6'>{formatDate(customerPODate)}</p>
                                        </div>
                                    </div>
                                </>
                            )}
                            <div className='col-md-4'>
                                <div className='mb-3'>
                                    <label className='form-label text-muted fw-semibold'>GSPL GSTIN</label>
                                    <p className='mb-0 fs-6'>{gsplGSTNumber || '-'}</p>
                                </div>
                            </div>
                            <div className='col-md-4'>
                                <div className='mb-3'>
                                    <label className='form-label text-muted fw-semibold'>Currency</label>
                                    <p className='mb-0 fs-6'>{currencyOfTransaction || '-'}</p>
                                </div>
                            </div>
                            <div className='col-md-4'>
                                <div className='mb-3'>
                                    <label className='form-label text-muted fw-semibold'>Warranty Period (from commissioning)</label>
                                    <p className='mb-0 fs-6'>{warrantyPeriod ? `${warrantyPeriod} months` : '-'}</p>
                                </div>
                            </div>
                            <div className='col-md-4'>
                                <div className='mb-3'>
                                    <label className='form-label text-muted fw-semibold'>Warranty Terms (from supply)</label>
                                    <p className='mb-0 fs-6'>{warrantyTerms ? `${warrantyTerms} months` : '-'}</p>
                                </div>
                            </div>
                            <div className='col-md-6'>
                                <div className='mb-3'>
                                    <label className='form-label text-muted fw-semibold'>Billing Address</label>
                                    <p className='mb-0 fs-6'>{billingAddress || '-'}</p>
                                </div>
                            </div>
                            <div className='col-md-6'>
                                <div className='mb-3'>
                                    <label className='form-label text-muted fw-semibold'>Shipping Address</label>
                                    <p className='mb-0 fs-6'>{shippingAddress || billingAddress || '-'}</p>
                                </div>
                            </div>
                        </div>

                        {/* Line Items Section */}
                        {lineItems && lineItems.length > 0 && (
                            <div className='row g-3 mt-3'>
                                <div className='col-12'>
                                    <h5 className='fw-semibold mb-3'>Line Items</h5>
                                    <div className='table-responsive'>
                                        <table className='table table-bordered table-hover'>
                                            <thead className='table-light'>
                                                <tr>
                                                    <th>Product Line</th>
                                                    <th>Model Name</th>
                                                    <th>Part Code</th>
                                                    <th>HSN Number</th>
                                                    <th>UOM</th>
                                                    <th>Quantity</th>
                                                    <th>Lead Time</th>
                                                    <th>Delivery Date</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {lineItems.map((item, index) => (
                                                    <tr key={index}>
                                                        <td>{item?.model?.productLine?.productLineName || '-'}</td>
                                                        <td>{item?.model?.modelName || '-'}</td>
                                                        <td>{item?.model?.partCode || '-'}</td>
                                                        <td>{item?.model?.HSNNumber || '-'}</td>
                                                        <td>{item?.model?.uom || '-'}</td>
                                                        <td>{item?.quantity || 0}</td>
                                                        <td>{item?.standardLeadTime || '-'}</td>
                                                        <td>{formatDate(item?.committedDeliveryDate)}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        )}

                        <div className='d-flex align-items-center mt-2'>
                            <span className='badge bg-success'>
                                <i className='bi bi-check-circle me-1'></i> Agreement Reached - Won
                            </span>
                        </div>
                    </div>
                ) : (
                    // Form View - Editable
                    <form className='row g-3 needs-validation' noValidate>
                        <div className={isPOAvailable === 'true' ? 'col-md-4' : 'col-md-6'}>
                            <label htmlFor='name' className='form-label'>
                                Zero Date Delivery <span className='text-danger'>*</span>
                            </label>
                            <input
                                value={zeroDateOfDelivery}
                                onChange={e => setZeroOfDateDelivery(e.target.value)}
                                name='meetingDate'
                                min='2022-01-01'
                                max='2099-12-31'
                                className='form-control'
                                type='date'
                                id='meetingDate'
                                onClick={e => e.target.showPicker && e.target.showPicker()}
                            />
                        </div>
                        <div className={isPOAvailable === 'true' ? 'col-md-4' : 'col-md-6'}>
                            <label htmlFor='isPOAvailable' className='form-label'>
                                Is PO Available? <span className='text-danger'>*</span>
                            </label>
                            <select
                                id='isPOAvailable'
                                value={isPOAvailable}
                                onChange={e => setIsPoAvailable(e.target.value)}
                                className='form-select'
                                required
                            >
                                <option value='true'>Yes</option>
                                <option value='false'>No</option>
                            </select>
                        </div>
                        {isPOAvailable === 'true' ? (
                            <>
                                <div className='col-md-4'>
                                    <label htmlFor='name' className='form-label'>
                                        Customer PO Number <span className='text-danger'>*</span>
                                    </label>
                                    <input
                                        value={customerPONumber}
                                        onChange={e => setCustomerPONumber(e.target.value)}
                                        type='text'
                                        className='form-control'
                                        required
                                    />
                                </div>
                                <div className='col-md-12'>
                                    <label htmlFor='name' className='form-label'>
                                        Upload PO <span className='text-danger'>*</span>
                                    </label>
                                    <input
                                        ref={poFileInputRef}
                                        type='file'
                                        className='form-control'
                                        required
                                    />
                                </div>

                                <div className='col-md-4'>
                                    <label htmlFor='name' className='form-label'>
                                        Customer PO Date <span className='text-danger'>*</span>
                                    </label>
                                    <input
                                        value={customerPODate}
                                        onChange={e => setCustomerPODate(e.target.value)}
                                        name='meetingDate'
                                        min='2022-01-01'
                                        max='2099-12-31'
                                        className='form-control'
                                        type='date'
                                        id='op date'
                                        onClick={e => e.target.showPicker && e.target.showPicker()}
                                    />
                                </div>
                            </>
                        ) : (
                            <div className='col-md-12'>
                                <label htmlFor='name' className='form-label'>
                                    Po Details <span className='text-danger'>*</span>
                                </label>
                                <textarea
                                    type='text'
                                    value={PODetails}
                                    onChange={e => setPoDetails(e.target.value)}
                                    className='form-control'
                                    required
                                />
                            </div>
                        )}
                        <div className={isPOAvailable === 'true' ? 'col-md-4' : 'col-md-6'}>
                            <label htmlFor='name' className='form-label'>
                                GSPL GSTIN <span className='text-danger'>*</span>
                            </label>
                            <select
                                className='form-select'
                                value={gsplGSTNumber}
                                onChange={e => setGsplGSTNumber(e.target.value)}
                            >
                                <option value=''>Select</option>
                                <option value='19AAECG1743K1ZG'>19AAECG1743K1ZG</option>
                                <option value='18AAECG1743K1ZI'>18AAECG1743K1ZI</option>
                            </select>
                        </div>
                        <div className={isPOAvailable === 'true' ? 'col-md-4' : 'col-md-6'}>
                            <label htmlFor='name' className='form-label'>
                                Consultant (If Any)
                            </label>
                            <input
                                value={consultant}
                                onChange={e => setConsultant(e.target.value)}
                                type='text'
                                className='form-control'
                                required
                            />
                        </div>
                        <div className='col-md-4'>
                            <label htmlFor='name' className='form-label'>
                                Currency Of Transaction <span className='text-danger'>*</span>
                            </label>
                            <select
                                className='form-select'
                                value={currencyOfTransaction}
                                onChange={e => setCurrencyOfTransaction(e.target.value)}
                            >
                                <option value=''>Select</option>
                                <option value='INR'>INR</option>
                                <option value='USD'>USD</option>
                            </select>
                        </div>
                        <div className='col-md-8'>
                            <label htmlFor='name' className='form-label'>
                                Warranty Period from date of commissioning (in months)
                                <span className='text-danger'>*</span>
                            </label>
                            <input
                                value={warrantyPeriod}
                                onChange={e => setWarrantyPeriod(e.target.value)}
                                type='number'
                                onWheel={e => e.target.blur()}
                                className='form-control'
                                required
                            />
                        </div>
                        <div className='col-md-8'>
                            <label htmlFor='name' className='form-label'>
                                Warranty Period from date of last supply (in months)
                                <span className='text-danger'>*</span>
                            </label>
                            <input
                                type='number'
                                onWheel={e => e.target.blur()}
                                value={warrantyTerms}
                                onChange={e => setWarrantyTerms(e.target.value)}
                                className='form-control'
                                required
                            />
                        </div>
                        <div className='col-md-4'>
                            <label htmlFor='name' className='form-label'>
                                Price Basis <span className='text-danger'>*</span>
                            </label>
                            <select
                                className='form-select'
                                value={priceBasis}
                                onChange={e => setPriceBasis(e.target.value)}
                            >
                                <option value=''>Select</option>
                                <option value='Ex- Works Factory'>Ex- Works Factory</option>
                                <option value='Ex- Vendor Works'>Ex- Vendor Works</option>
                                <option value='FOR Site'>FOR Site</option>
                                <option value='Ex- Godown Bardhaman'>Ex- Godown Bardhaman</option>
                                <option value='Ex- Godown Kolkata'>Ex- Godown Kolkata</option>
                                <option value='Ex- Godown Siliguri'>Ex- Godown Siliguri</option>
                                <option value='Ex- Godown Guwahati'>Ex- Godown Guwahati</option>
                            </select>
                        </div>
                        <div className='col-md-4'>
                            <label htmlFor='name' className='form-label'>
                                Responsibility of transportation{' '}
                                <span className='text-danger'>*</span>
                            </label>
                            <select
                                className='form-select'
                                value={responsibilityOfTransportation}
                                onChange={e => setResponsibilityOfTransportation(e.target.value)}
                            >
                                <option value=''>Select</option>
                                <option value='GSPL'>GSPL</option>
                                <option value='Customer'>Customer</option>
                            </select>
                        </div>
                        <div className='col-md-4'>
                            <label htmlFor='name' className='form-label'>
                                Type of Transaction <span className='text-danger'>*</span>
                            </label>
                            <select
                                className='form-select'
                                value={typeOfTransaction}
                                onChange={e => setTypeOfTransaction(e.target.value)}
                            >
                                <option value=''>Select</option>
                                <option value='Domestic'>Domestic</option>
                                <option value='Export'>Export</option>
                                <option value='Indirect Export'>Indirect Export</option>
                                <option value='EPCG'>EPCG</option>
                                <option value='Tax exempted'>Tax exempted</option>
                            </select>
                        </div>
                        <div className='col-md-12'>
                            <label htmlFor='name' className='form-label'>
                                Bank Gurraantee Details <span className='text-danger'>*</span>
                            </label>
                            <Select
                                options={bankGuaranteeDetailsData}
                                onChange={selectedOptions =>
                                    setbankGuaranteeDetails(
                                        selectedOptions
                                            ? selectedOptions.map(option => option.label)
                                            : []
                                    )
                                }
                                isMulti={true}
                            />
                        </div>
                        <div className='col-md-12'>
                            <label htmlFor='name' className='form-label'>
                                Billing Address <span className='text-danger'>*</span>
                            </label>
                            <textarea
                                value={billingAddress}
                                onChange={e => setBillingAddress(e.target.value)}
                                type='text'
                                className='form-control'
                                required
                            />
                        </div>
                        <div className='col-md-12'>
                            <div className='form-check'>
                                <input
                                    className='form-check-input'
                                    type='checkbox'
                                    checked={checkShipping}
                                    onChange={e => {
                                        setCheckShipping(e.target.checked)
                                        if (e.target.checked) {
                                            setShippingAddress(billingAddress)
                                        } else {
                                            setShippingAddress('')
                                        }
                                    }}
                                    id='flexCheckDefault'
                                />
                                <label className='form-check-label' htmlFor='flexCheckDefault'>
                                    Shipping address same as billing address?
                                </label>
                            </div>
                        </div>
                        <div className='col-md-12'>
                            <label htmlFor='name' className='form-label'>
                                Shipping Address <span className='text-danger'>*</span>
                            </label>
                            <textarea
                                value={checkShipping ? billingAddress : shippingAddress}
                                onChange={e => {
                                    if (!checkShipping) {
                                        setShippingAddress(e.target.value)
                                    }
                                }}
                                type='text'
                                className='form-control'
                                disabled={checkShipping}
                                required
                            />
                        </div>
                        <div className='col-md-12'>
                            <label htmlFor='name' className='form-label'>
                                Remarks
                            </label>
                            <textarea
                                value={remarks}
                                onChange={e => setRemarks(e.target.value)}
                                type='text'
                                className='form-control'
                                required
                            />
                        </div>
                        {focLeadDetails?.productRequirements?.natureOfSale === 'Project' ? (
                            <>
                                <div className='col-md-12'>
                                    <label htmlFor='name' className='form-label'>
                                        BOM <span className='text-danger'>*</span>
                                    </label>
                                    <input
                                        ref={bomFileInputRef}
                                        type='file'
                                        className='form-control'
                                        required
                                    />
                                </div>
                            </>
                        ) : (
                            ''
                        )}
                        <div className='col-md-12'>
                            <h4 className='text-lg font-semibold mb-3'>Payment Terms</h4>
                        </div>
                        {paymentTerms.map((term, index) => (
                            <Fragment key={index}>
                                <div className='col-md-6 mb-3'>
                                    <label className='form-label'>
                                        Payment Type <span className='text-danger'>*</span>
                                    </label>
                                    <select
                                        className='form-select'
                                        value={term.type}
                                        onChange={e =>
                                            handlePaymentTermChange(index, 'type', e.target.value)
                                        }
                                    >
                                        <option value=''>Select</option>
                                        <option>Advance along with Purchase order</option>
                                        <option>Payment against submission of drawings</option>
                                        <option>
                                            Payment against proof of placement of Purchase order to
                                            vendor
                                        </option>
                                        <option>
                                            Payment against pro-forma invoice before despatch along with
                                            100% GST
                                        </option>
                                        <option>
                                            Payment against pro-forma invoice before despatch along with
                                            prorata GST
                                        </option>
                                        <option>Payment against despatch document</option>
                                        <option>Payment on receipt of material at site</option>
                                        <option>Payment on stage completion of erection</option>
                                        <option>Payment against completion of erection</option>
                                        <option>Payment against completion of commissioning</option>
                                        <option>Payment on completion of DLP</option>
                                        <option>
                                            Payment within 7/15/21/30/45/60/90 days from date of receipt
                                            of material
                                        </option>
                                    </select>
                                </div>

                                <div className='col-md-2 mb-3'>
                                    <label className='form-label'>
                                        Percentage <span className='text-danger'>*</span>
                                    </label>
                                    <input
                                        type='number'
                                        onWheel={e => e.target.blur()}
                                        className='form-control'
                                        value={term.percentage}
                                        onChange={e =>
                                            handlePaymentTermChange(index, 'percentage', e.target.value)
                                        }
                                        disabled
                                        title="FOC payment terms are always 100%"
                                    />
                                </div>

                                <div className='col-md-2 mb-3'>
                                    <label className='form-label'>
                                        Amount <span className='text-danger'>*</span>
                                    </label>
                                    <input
                                        type='number'
                                        onWheel={e => e.target.blur()}
                                        className='form-control'
                                        value={term.amount}
                                        onChange={e =>
                                            handlePaymentTermChange(index, 'amount', e.target.value)
                                        }
                                        disabled
                                        title="FOC payment amount is always 0 (Free of Cost)"
                                    />
                                </div>

                                <div className='col-md-2 col-12 mb-3 d-flex align-items-end justify-content-center gap-2'>
                                    {/* Add Button */}
                                    <button
                                        type='button'
                                        className='btn add-btn text-white d-flex justify-content-center align-items-center'
                                        onClick={addPaymentTerm}
                                    >
                                        +
                                    </button>

                                    {/* Delete Button → Only show for index > 0 */}
                                    {index > 0 && (
                                        <button
                                            type='button'
                                            className='btn btn-danger d-flex justify-content-center align-items-center'
                                            onClick={() => removePaymentTerm(index)}
                                        >
                                            -
                                        </button>
                                    )}
                                </div>
                            </Fragment>
                        ))}
                        <div className='col-md-6'>
                            <label htmlFor='name' className='form-label'>
                                Select Commercial Manager <span className='text-danger'>*</span>
                            </label>
                            <Select
                                options={commercialManagerDetails}
                                placeholder='Select Commercial Manager'
                                value={
                                    commercialManagerDetails.find(option => option.value === commercialManager) || null
                                }
                                onChange={e => setCommercialManager(e ? e.value : '')}
                                isClearable
                            />
                        </div>
                        <div className='col-md-12 d-flex justify-content-end'>
                            <button
                                onClick={handleSubmit}
                                className='btn add-btn text-white'
                                type='button'
                                disabled={submitting}
                            >
                                {submitting ? 'Submitting...' : (isEditing ? 'Update Sales Order' : 'Submit')}
                            </button>
                        </div>
                    </form>
                )}

                {/* Success Modal */}
                <Modal show={showSuccessModal} centered>
                    <Modal.Body>
                        <div className='my-8 text-center'>
                            <svg
                                xmlns='http://www.w3.org/2000/svg'
                                className='w-14 shrink-0 fill-green-500 inline'
                                viewBox='0 0 512 512'
                            >
                                <path d='M383.841 171.838c-7.881-8.31-21.02-8.676-29.343-.775L221.987 296.732l-63.204-64.893c-8.005-8.213-21.13-8.393-29.35-.387-8.213 7.998-8.386 21.137-.388 29.35l77.492 79.561a20.687 20.687 0 0 0 14.869 6.275 20.744 20.744 0 0 0 14.288-5.694l147.373-139.762c8.316-7.888 8.668-21.027.774-29.344z' />
                                <path d='M256 0C114.84 0 0 114.84 0 256s114.84 256 256 256 256-114.84 256-256S397.16 0 256 0zm0 470.487c-118.265 0-214.487-96.214-214.487-214.487 0-118.265 96.221-214.487 214.487-214.487 118.272 0 214.487 96.221 214.487 214.487 0 118.272-96.215 214.487-214.487 214.487z' />
                            </svg>
                            <h4 className='text-xl text-gray-800 font-semibold mt-4'>Good Job</h4>
                            <p className='text-sm text-gray-500 leading-relaxed mt-4'>{modalMessage}!</p>
                        </div>

                        <button
                            type='button'
                            onClick={handleCloseModal}
                            className='px-5 py-2.5 w-full rounded-lg text-white text-sm border-none outline-none bg-green-500 hover:bg-green-700'
                        >
                            Okay
                        </button>
                    </Modal.Body>
                </Modal>

                {/* Error Modal */}
                <Modal show={showDeclineModal} centered>
                    <Modal.Body>
                        <div className='my-8 text-center'>
                            <svg
                                xmlns='http://www.w3.org/2000/svg'
                                className='w-14 fill-red-500 inline'
                                viewBox='0 0 286.054 286.054'
                            >
                                <path
                                    fill='#e2574c'
                                    d='M143.027 0C64.04 0 0 64.04 0 143.027c0 78.996 64.04 143.027 143.027 143.027 78.996 0 143.027-64.022 143.027-143.027C286.054 64.04 222.022 0 143.027 0zm0 259.236c-64.183 0-116.209-52.026-116.209-116.209S78.844 26.818 143.027 26.818s116.209 52.026 116.209 116.209-52.026 116.209-116.209 116.209zm.009-196.51c-10.244 0-17.995 5.346-17.995 13.981v79.201c0 8.644 7.75 13.972 17.995 13.972 9.994 0 17.995-5.551 17.995-13.972V76.707c-.001-8.43-8.001-13.981-17.995-13.981zm0 124.997c-9.842 0-17.852 8.01-17.852 17.86 0 9.833 8.01 17.843 17.852 17.843s17.843-8.01 17.843-17.843c-.001-9.851-8.001-17.86-17.843-17.86z'
                                ></path>
                            </svg>
                            <h4 className='text-xl text-gray-800 font-semibold mt-4'>Error Occurred</h4>
                            <p className='text-sm text-gray-500 leading-relaxed mt-4'>{modalMessage}!</p>
                        </div>

                        <button
                            type='button'
                            onClick={handleCloseModal}
                            className='px-5 py-2.5 w-full rounded-lg text-white text-sm border-none outline-none bg-red-500 hover:bg-red-700'
                        >
                            Okay
                        </button>
                    </Modal.Body>
                </Modal>
                <Toast ref={toast} />
            </div>
        </Fragment>
    )
}

export default FocReachedAgreement
